import axios from 'axios';
import { auth, fireStoreDB } from './firebaseConfig';
import * as firebaseAuth from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  setDoc,
  getDoc,
  addDoc,
  deleteDoc,
  getFirestore,
  limit,
  startAfter,
  getCountFromServer,
} from 'firebase/firestore';
import { getProfile, setSecondaryProfile } from 'utilities';

export const getDashboardStats = async (type, users) => {
  try {
    const userData = getProfile();
    const uid = userData?.uid || auth?.currentUser?.uid;

    let userIds = [];

    if (users && users.length > 0) {
      userIds = users.map((user) => user.id);
    } else if (uid) {
      userIds = [uid];
    } else {
      console.warn(`[getDashboardStats] No user ID for ${type}`);
      return [];
    }

    const querySnapshot = await getDocs(
      query(
        collection(fireStoreDB, type),
        where('userIds', 'array-contains-any', userIds)
      )
    );

    const records = querySnapshot.docs.map((doc) => doc.data());
    return records || [];
  } catch (error) {
    console.error(`[getDashboardStats] Error fetching ${type}:`, error.message || error);
    return [];
  }
};

export const getDashboardDocumentStats = async (type) => {
  const userData = getProfile();
  const querySnapshot = await getDocs(
    query(
      collection(fireStoreDB, type),
      where('email', '==', userData.reloadUserInfo.email)
    )
  );
  const records = querySnapshot.docs.map((doc) => doc.data());
  return records || [];
};
let tokenGenerationTime = 0;
let accessToken = null;
let tokenFetchPromise = null;

async function getAccessToken() {
  const currentTime = Date.now();
  // Return cached token if still valid (1 hour)
  if (accessToken && currentTime - tokenGenerationTime < 3600 * 1000) {
    return accessToken;
  }
  // Deduplicate concurrent requests — reuse in-flight fetch
  if (tokenFetchPromise) {
    return tokenFetchPromise;
  }
  tokenFetchPromise = (async () => {
    const url = 'https://getaccessv3-s2b2i3uzya-uc.a.run.app';
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000); // 8s timeout
      const response = await fetch(url, { method: 'GET', signal: controller.signal });
      clearTimeout(timeout);
      if (!response.ok) {
        console.error('[WHO] Token fetch failed:', response.status);
        return accessToken || null; // Return stale token if available
      }
      const data = await response.text();
      accessToken = data;
      tokenGenerationTime = Date.now();
      return accessToken;
    } catch (error) {
      console.error('[WHO] Token fetch error:', error.message);
      return accessToken || null; // Return stale token if available
    } finally {
      tokenFetchPromise = null;
    }
  })();
  return tokenFetchPromise;
}

// Preload WHO API token on module load so it's ready when user reaches diagnoses
getAccessToken().catch(() => {});

// WHO's ICD-11 search API wraps the part of a title that matched the query in
// highlight markup, e.g. "infection by <em class='found'>salmonella</em> typhi".
// We used to split on those tags and join with a space, which inserted a space
// wherever the match ended -- that is how "Crohn disease" came to be stored as
// "Cro hn disease" and "Haemophilia A" as "Hae mophilia A". Remove the tags and
// put nothing in their place, then tidy the whitespace the markup left behind.
const stripIcdHighlighting = (title = '') =>
  String(title)
    .replace(/<\/?em[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:)])/g, '$1')
    .trim();

export const getAllProfileRecords = async (type) => {
  if (type === 'diagnoses') {
    try {
      const token = await getAccessToken();
      if (!token) {
        console.error('[WHO] No access token available for diagnoses search');
        return [];
      }
      let headersList = {
        'API-Version': 'v2',
        'Accept-Language': 'en',
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${token}`,
      };
      let reqOptions = {
        url: 'https://id.who.int/icd/release/11/2024-01/mms/search',
        method: 'GET',
        headers: headersList,
        params: {
          q: localStorage.getItem('search-text')
            ? `${localStorage.getItem('search-text')}%`
            : '%a%',
          highlightingEnabled: false,
        },
        timeout: 10000, // 10s timeout for WHO API
      };

      let response = await axios.request(reqOptions);

      const entities = response?.data?.destinationEntities;
      if (!entities || !Array.isArray(entities)) {
        console.warn('[WHO] No destination entities in response');
        return [];
      }
      const res = entities.map((item) => {
        return {
          description: `${item?.theCode} - ${stripIcdHighlighting(item.title)}`,
          userIds: [],
        };
      });
      let searchText = localStorage.getItem('search-text');
      if (searchText && searchText.length > 0) {
        return res.filter((item) =>
          item.description.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      return res;
    } catch (error) {
      console.error('[WHO] Diagnoses search error:', error.message || error);
      return [];
    }
  } else {
    if (type != undefined && type != 'undefined') {
      try {
        console.log(`[Medical] Fetching ${type} from Firestore...`);
        const querySnapshot = await getDocs(collection(fireStoreDB, type));
        console.log(`[Medical] ${type}: got ${querySnapshot.docs.length} docs`);
        const response = querySnapshot.docs.map((doc) => doc.data()) || [];
        var newArray = response.filter(
          (value) => Object.keys(value).length !== 0
        );
        var test = newArray.filter((item) => 'description' in item);
        console.log(`[Medical] ${type}: ${test.length} items with descriptions`);
        return test;
      } catch (error) {
        console.error(`[Medical] Error fetching ${type}:`, error.message || error);
        return [];
      }
    }

    return [];
  }
  // fetch("https://icd11restapi-developer-test.azurewebsites.net/icd/release/11/2024-01/mms/search", {
  //   "headers": {
  //     "accept": "application/json",
  //     "accept-language": "en",
  //     "api-version": "v2",
  //     "content-type": "multipart/form-data; boundary=----WebKitFormBoundaryW1n9bAtmPZ91JDb7",
  //     "priority": "u=1, i",
  //     "sec-ch-ua": "\"Chromium\";v=\"124\", \"Opera\";v=\"110\", \"Not-A.Brand\";v=\"99\"",
  //     "sec-ch-ua-mobile": "?0",
  //     "sec-ch-ua-platform": "\"Windows\"",
  //     "sec-fetch-dest": "empty",
  //     "sec-fetch-mode": "cors",
  //     "sec-fetch-site": "cross-site",
  //     "Referer": "https://cdpn.io/",
  //     "Referrer-Policy": "strict-origin-when-cross-origin"
  //   },
  //   "body": "------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"q\"\r\n\r\nabs%\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"chapterFilter\"\r\n\r\n10;11;12;13;14;15;16;17;18;19;20;21;22;23;24;25;01;02;03;04;05;06;07;08;09;\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"subtreesFilter\"\r\n\r\n\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"includePostcoordination\"\r\n\r\ntrue\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"useBroaderSynonyms\"\r\n\r\nfalse\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"useFlexiSearch\"\r\n\r\nfalse\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"includeKeywordResult\"\r\n\r\ntrue\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"flatResults\"\r\n\r\ntrue\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"highlightingEnabled\"\r\n\r\ntrue\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7\r\nContent-Disposition: form-data; name=\"medicalCodingMode\"\r\n\r\ntrue\r\n------WebKitFormBoundaryW1n9bAtmPZ91JDb7--\r\n",
  //   "method": "POST"
  // }); ;
};

export const getProfileRecords = async (
  collectionName = 'diagnoses',
  companyName
) => {
  try {
    // ✅ Special case for "diagnoses" (keep your existing ICD logic)
    if (collectionName === 'diagnoses') {
      const token = await getAccessToken();
      const headersList = {
        'API-Version': 'v2',
        'Accept-Language': 'en',
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${token}`,
      };

      const reqOptions = {
        url: 'https://id.who.int/icd/release/11/2024-01/mms/search',
        method: 'GET',
        headers: headersList,
        params: {
          q: localStorage.getItem('search-text')
            ? `${localStorage.getItem('search-text')}%`
            : '%a%',
          highlightingEnabled: false,
        },
      };

      const response = await axios.request(reqOptions);
      const res =
        response?.data?.destinationEntities.map((item) => ({
          description: `${item?.theCode} - ${stripIcdHighlighting(item.title)}`,
          userIds: [],
        })) || [];

      const searchText = localStorage.getItem('search-text');
      if (searchText && searchText.length > 0) {
        return res.filter((item) =>
          item.description.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      return res;
    }

    // ✅ If Firestore Collection (like "users")
    if (collectionName) {
      let q;

      // If a companyName is provided, filter by it
      if (companyName) {
        q = query(
          collection(fireStoreDB, collectionName),
          where('companyName', '==', companyName)
        );
      } else {
        // Otherwise fetch all documents
        q = collection(fireStoreDB, collectionName);
      }

      const querySnapshot = await getDocs(q);

      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return data;
    }

    return [];
  } catch (error) {
    console.error('Error fetching profile records:', error);
    return [];
  }
};
export const addNewItem = async (type, description) => {
  try {
    const userData = getProfile();
    const uid = userData?.uid || auth?.currentUser?.uid;
    if (!uid) {
      console.error('[addNewItem] No user ID available');
      return false;
    }
    console.log(`[addNewItem] Adding ${type}: "${description}" for user ${uid}`);

    const querySnapshot = await getDocs(
      query(
        collection(fireStoreDB, type),
        where('description', '==', description)
      )
    );

    let docData, docID;
    if (querySnapshot.docs[0]) {
      docData = querySnapshot.docs[0].data();
      docID = querySnapshot.docs[0].id;
    } else {
      const docRef = await addDoc(collection(fireStoreDB, type), {
        description,
        userIds: [],
      });
      docID = docRef.id;
      docData = { description, userIds: [] };
    }

    const documentRef = doc(fireStoreDB, type, docID);
    if (
      !(docData && docData?.userIds && docData?.userIds?.includes(uid))
    ) {
      await updateDoc(documentRef, {
        userIds:
          docData?.userIds && docData?.userIds.length > 0
            ? [...docData?.userIds, uid]
            : [uid],
      });
      console.log(`[addNewItem] Successfully added ${type}: "${description}"`);
      return true;
    } else {
      console.log(`[addNewItem] ${type}: "${description}" already exists for user`);
      return false;
    }
  } catch (error) {
    console.error(`[addNewItem] Error adding ${type}:`, error.message || error);
    return false;
  }
};
export const addNewDocument = async (requestJson) => {
  try {
    const collectionRef = collection(fireStoreDB, 'documents');
    const newDocRef = await addDoc(collectionRef, requestJson);
    return true;
  } catch ({ message }) {
    console.log('Error in adding new Document : ', message);
    return false;
  }
};
export const deleteItem = async (type, description) => {
  try {
    const userData = getProfile();
    const uid = userData?.uid || auth?.currentUser?.uid;
    const querySnapshot = await getDocs(
      query(
        collection(fireStoreDB, type),
        where('description', '==', description)
      )
    );
    if (!querySnapshot.docs[0]) {
      console.warn(`[deleteItem] No doc found for ${type}: "${description}"`);
      return false;
    }
    const docData = querySnapshot.docs[0].data();
    const docID = querySnapshot.docs[0].id;
    const documentRef = doc(fireStoreDB, type, docID);

    await updateDoc(documentRef, {
      userIds:
        docData?.userIds && docData?.userIds.length > 1
          ? docData?.userIds.filter((item) => item !== uid)
          : [],
    });
    return true;
  } catch (error) {
    console.error(`[deleteItem] Error deleting ${type}:`, error.message || error);
    return false;
  }
};
export const getUserDetails = async (liveUID) => {
  const documentRef = doc(fireStoreDB, `user`, liveUID);
  const res = await getDoc(documentRef);
  return res.data();
};

// 🆕 Helper to check businesses collection
export const getBusinessDetails = async (uid) => {
  const q = query(
    collection(fireStoreDB, 'businesses'),
    where('userId', '==', uid)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty ? snapshot.docs[0].data() : null;
};

export const getUsersByCompany = async (companyName) => {
  try {
    const db = getFirestore();

    // Reference to the "users" collection
    const usersRef = collection(db, 'user');

    // Build query: only users where company == companyName

    const q = query(
      usersRef,
      where('company', '==', companyName.toLowerCase())
    );

    // Execute query
    const querySnapshot = await getDocs(q);

    // Map results into array
    const users = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return users;
  } catch (error) {
    console.error('Error fetching users by company:', error);
    return [];
  }
};

/**
 * Paginated version of getUsersByCompany.
 * Returns 50 users at a time — call repeatedly with the returned lastDoc
 * to fetch subsequent pages. Reduces a 3000-document read to 50 per page load.
 *
 * ⚠️  Requires a Firestore composite index if you add orderBy:
 *     Collection: user | Fields: company ASC, lastName ASC
 *     Create it at: Firebase Console → Firestore → Indexes → Composite
 *
 * @param {string} companyName
 * @param {number} pageSize  — documents per page (default 50)
 * @param {DocumentSnapshot|null} lastDoc — cursor returned from the previous call
 * @returns {{ users: object[], lastDoc: DocumentSnapshot|null, hasMore: boolean }}
 */
export const getUsersByCompanyPaginated = async (companyName, pageSize = 50, lastDoc = null) => {
  try {
    const db = getFirestore();
    const usersRef = collection(db, 'user');
    const lowerName = companyName.toLowerCase();

    // Fetch one extra to detect whether there's a next page
    const constraints = [
      where('company', '==', lowerName),
      limit(pageSize + 1),
    ];
    if (lastDoc) constraints.push(startAfter(lastDoc));

    const querySnapshot = await getDocs(query(usersRef, ...constraints));
    const hasMore = querySnapshot.docs.length > pageSize;
    const docs    = hasMore ? querySnapshot.docs.slice(0, pageSize) : querySnapshot.docs;

    return {
      users:   docs.map((d) => ({ id: d.id, ...d.data() })),
      lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error('Error in getUsersByCompanyPaginated:', error);
    return { users: [], lastDoc: null, hasMore: false };
  }
};

/**
 * Returns employee counts without reading any documents.
 * Uses Firestore's getCountFromServer() aggregation — 0 document reads,
 * so it doesn't count against the 50k/day free-tier quota.
 *
 * @param {string} companyName
 * @returns {{ total: number, ordered: number, reordered: number }}
 */
export const getCompanyEmployeeStats = async (companyName) => {
  try {
    const db = getFirestore();
    const usersRef = collection(db, 'user');
    const lowerName = companyName.toLowerCase();

    const [totalSnap, orderedSnap, reorderedSnap] = await Promise.all([
      getCountFromServer(query(usersRef, where('company', '==', lowerName))),
      getCountFromServer(query(usersRef, where('company', '==', lowerName), where('cardOrderStatus',   '==', 'Registered'))),
      getCountFromServer(query(usersRef, where('company', '==', lowerName), where('cardReOrderStatus', '==', 'Registered'))),
    ]);

    return {
      total:     totalSnap.data().count,
      ordered:   orderedSnap.data().count,
      reordered: reorderedSnap.data().count,
    };
  } catch (error) {
    console.error('Error in getCompanyEmployeeStats:', error);
    return { total: 0, ordered: 0, reordered: 0 };
  }
};

export const saveProfileImage = async (newImageURL, liveUID) => {
  const userData = getProfile();
  const { uid } = userData;
  const documentRef = doc(fireStoreDB, `user`, uid || liveUID);
  await updateDoc(documentRef, {
    avatar: newImageURL,
  });
  return true;
};
export const saveUserProfile = async (requestObject) => {
  // Prefer the authenticated uid; the cached profile can be empty (e.g. when
  // the post-payment finalize didn't complete), which previously crashed here.
  const uid = getProfile()?.uid || auth.currentUser?.uid;
  if (!uid) throw new Error('saveUserProfile: no authenticated user');
  const documentRef = doc(fireStoreDB, `user`, uid);
  // Drop undefined values (setDoc rejects them — e.g. profileId before the
  // profile was ever persisted) and backfill profileId from the uid.
  const clean = {};
  for (const [k, val] of Object.entries(requestObject)) if (val !== undefined) clean[k] = val;
  if (!clean.profileId) clean.profileId = uid;
  // merge: create the doc if the finalize never wrote it, update it otherwise.
  await setDoc(documentRef, clean, { merge: true });
  setSecondaryProfile(await getUserDetails(uid));
};
export const savePersonalDetail = async () => {
  const userData = getProfile();
  const { uid } = userData;
  const documentRef = doc(fireStoreDB, `user`, uid);
  const res = await getDoc(documentRef);
  return res.data();
};
export const performLoginThroughFirebase = async ({ email, password }) => {
  try {
    const responseObject = await firebaseAuth.signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    return responseObject;
  } catch (error) {
    // Throw the actual error so try/catch in performLogin handles it
    throw error;
  }
};

export const performSignUpThroughFirebase = async ({ email, password }) => {
  try {
    // Check if email already exists
    const emailExists = await checkIfEmailExists(email);

    if (!emailExists) {
      const response = await firebaseAuth.signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      return response;
    }
    const response = await firebaseAuth.createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const performBusinessSignUpThroughFirebase = async ({
  email,
  password,
  businessData, // { companyName, website, industry, ... }'
  user,
}) => {
  try {
    const db = getFirestore();

    // 🔹 Step 2: Check if company name OR website already exists
    const businessRef = collection(db, 'businesses');
    const emailExists = await checkIfEmailExists(email);
    // emailExists check (logging removed for security)
    // Search for existing businesses with same company name or website
    const nameQuery = query(
      businessRef,
      where('companyName', '==', businessData.companyName.trim())
    );

    const [nameSnapshot, websiteSnapshot] = await Promise.all([
      getDocs(nameQuery),
    ]);

    if (!nameSnapshot.empty) {
      throw new Error('A business with this company name already exists.');
    }

    // 🔹 Step 4: Save business data in Firestore
    await addDoc(businessRef, {
      userId: user.uid,
      email,
      ...businessData,
      createdAt: new Date(),
    });

    return true;
  } catch (error) {
    console.error('Business signup error:', error.message);
    return { error: error.message };
  }
};
export const performForgetPasswordThroughFirebase = async ({ email }) => {
  try {
    const response = await firebaseAuth.sendPasswordResetEmail(auth, email);
    return true;
  } catch (error) {
    return error.message;
  }
};
export const saveUserData = async (liveUID, userData) => {
  const documentRef = doc(fireStoreDB, 'user', liveUID);
  try {
    const response = await setDoc(documentRef, userData);
    return response;
  } catch (error) {
    console.error('Error saving user data');
    return error.message;
  }
};

export const updateUserData = async (liveUID, userData) => {
  const documentRef = doc(fireStoreDB, `user`, liveUID);
  await updateDoc(documentRef, {
    cardOrderDate: userData?.cardOrderDate,
    cardOrderStatus: userData?.cardOrderStatus,
  });
  return true;
};

export const updateUserReOrderData = async (liveUID, userData) => {
  const documentRef = doc(fireStoreDB, `user`, liveUID);
  await updateDoc(documentRef, {
    cardReOrderStatus: userData?.cardReOrderStatus,
    cardReOrderDate: userData?.cardReOrderDate,
  });
  return true;
};

export const checkIfEmailExists = (email) => {
  const response = firebaseAuth
    .fetchSignInMethodsForEmail(auth, email)
    .then(function (signInMethods) {
      if (signInMethods?.length === 0) {
        return true;
      } else {
        return false;
      }
    })
    .catch(function (error) {
      console.error('Error checking email existence: ', error);
      return true; // Assume available on error; createUserWithEmailAndPassword will catch duplicates
    });

  return response;
};

export const checkIfCompanyExists = async (businessData) => {
  try {
    if (!businessData?.companyName) {
      throw new Error('Missing company name or website.');
    }
    const db = getFirestore();

    const businessRef = collection(db, 'businesses');

    // Prepare both queries
    const nameQuery = query(
      businessRef,
      where('companyName', '==', businessData.companyName.trim())
    );

    // Run them in parallel
    const [nameSnapshot, websiteSnapshot] = await Promise.all([
      getDocs(nameQuery),
    ]);

    // Check for existing name
    if (!nameSnapshot.empty) {
      return {
        exists: true,
        field: 'companyName',
        message: 'A business with this company name already exists.',
      };
    }

    // ✅ If no duplicates found
    return {
      exists: false,
      message: 'No existing business found.',
    };
  } catch (error) {
    console.error('Error checking company existence:', error);
    return {
      exists: true,
      error: error.message || 'Unexpected error occurred.',
    };
  }
};
export const deleteUserAndData = async () => {
  const userData = getProfile();
  const liveUID = userData.uid;

  // Security: Only allow deleting the currently authenticated user's data
  // The 'id' parameter was removed to prevent IDOR attacks
  if (!liveUID || !auth.currentUser || auth.currentUser.uid !== liveUID) {
    console.error('Unauthorized: Cannot delete data for a different user');
    return false;
  }

  try {
    // Delete user document — always uses the authenticated user's UID
    await deleteDoc(doc(fireStoreDB, 'user', liveUID));

    const subscriptionsQuerySnapshot = await getDocs(
      query(
        collection(fireStoreDB, 'subscriptions'),
        where('user_id', '==', liveUID)
      )
    );
    const deleteSubscriptionsPromises = subscriptionsQuerySnapshot.docs.map(
      (doc) => deleteDoc(doc.ref)
    );
    await Promise.all(deleteSubscriptionsPromises);

    const trackOrderQuerySnapshot = await getDocs(
      query(
        collection(fireStoreDB, 'TrackOrder'),
        where('userID', '==', liveUID)
      )
    );
    const deleteTrackOrderPromises = trackOrderQuerySnapshot.docs.map((doc) =>
      deleteDoc(doc.ref)
    );
    await Promise.all(deleteTrackOrderPromises);

    // Delete user's data from 'diagnoses' collection
    // const diagnosesQuerySnapshot = await getDocs(collection(fireStoreDB, "diagnoses"));
    // const deleteDiagnosesPromises = [];
    // diagnosesQuerySnapshot.forEach((doc) => {
    //   const diagnosesData = doc.data();
    //   if (diagnosesData?.userIds && Array.isArray(diagnosesData?.userIds) && diagnosesData?.userIds?.includes(liveUID)) {
    //     deleteDiagnosesPromises.push(deleteDoc(doc.ref));
    //   }
    // });
    // await Promise.all(deleteDiagnosesPromises);

    // Delete user's data from 'allergies' collection
    //  const allergiesQuerySnapshot = await getDocs(collection(fireStoreDB, "allergies"));
    //  const deleteAllergiesPromises = [];
    //  allergiesQuerySnapshot.forEach((doc) => {
    //    const allergiesData = doc.data();
    //    if (allergiesData?.userIds && Array.isArray(allergiesData?.userIds) && allergiesData?.userIds?.includes(liveUID)) {
    //      deleteAllergiesPromises.push(deleteDoc(doc.ref));
    //    }
    //  });
    //  await Promise.all(deleteAllergiesPromises);

    // Finally, delete the user from authentication
    await firebaseAuth.deleteUser(auth.currentUser);

    // Logout user
    await firebaseAuth.signOut(auth);
    return true;
  } catch (error) {
    console.error('Error deleting user and data:', error);
    return false; // Deletion failed
  }
};

export const verifyEmailAdress = async (auth) => {
  try {
    await firebaseAuth.sendEmailVerification(auth.currentUser);
  } catch (error) {
    console.log(error);
  }
};

export const fetchBusinessData = async (userId) => {
  try {
    if (!userId) return null;

    const db = getFirestore();
    const businessRef = collection(db, 'businesses');
    const q = query(businessRef, where('userId', '==', userId));
    const snapshot = await getDocs(q);

    if (!snapshot.empty) {
      // If you have multiple businesses, map through them
      const businessList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Or if you only want the first one:
      // const business = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };

      return businessList;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching business data:', error);
    return null;
  }
};

export const updateBusinessData = async (businessId, updatedData) => {
  try {
    if (!businessId) throw new Error('Business ID is required');

    const db = getFirestore();
    const businessDocRef = doc(db, 'businesses', businessId);

    await updateDoc(businessDocRef, {
      ...updatedData,
    });

    return true;
  } catch (error) {
    console.error('Error updating business data:', error.message);
    return { error: error.message };
  }
};

export const fetchAllBusinesses = async () => {
  try {
    const db = getFirestore();
    const businessRef = collection(db, 'businesses');
    const snapshot = await getDocs(businessRef);

    if (!snapshot.empty) {
      const businesses = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return businesses;
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching all businesses:', error);
    return [];
  }
};

// export const fetchAllUserCreatedByCompanyName = async () => {
//   try {
//     const db = getFirestore();
//     const userRef = collection(db, "user");
//     const snapshot = await getDocs(userRef);

//     if (!snapshot.empty) {
//       const user = snapshot.docs.map((doc) => ({
//         id: doc.id,
//         ...doc.data(),
//       }));
//       console.log("All user:", user);
//       return user;
//     } else {
//       console.log("No user found");
//       return [];
//     }
//   } catch (error) {
//     console.error("Error fetching all user:", error);
//     return [];
//   }
// };

export const fetchBusinessUserForLinkedUser = async (email) => {
  if (!email || email.trim() === '') return null;

  try {
    const db = getFirestore();
    const userRef = collection(db, 'user'); // <-- your collection name
    const q = query(userRef, where('email', '==', email));

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return false;
    }

    // Return first matching user
    const docData = snapshot.docs[0];
    return {
      id: docData.id,
      ...docData.data(),
    };
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

// ======================================================
// 🔥 INVOICE FUNCTIONS
// ======================================================

// export const calculateInvoice = async (companyName) => {
//   const users = await getUsersByCompany(companyName);

//   const pricePerUser = users.reduce((total, user) => {
//     return total + (user.price || 0);
//   }, 0);

//   // Count users
//   const totalUsers = users.length;

//   // Count card orders
//   const totalCardOrders = users.filter(
//     user => user.cardOrderStatus === "ordered"
//   ).length;

//   const invoice = {
//     company: companyName,
//     totalUsers,
//     totalCardOrders,
//     totalAmount: pricePerUser,
//     month: new Date().toISOString().slice(0, 7),
//   };

//   return invoice;
// };

export const getUserByEmail = async (email) => {
  try {
    const q = query(
      collection(fireStoreDB, 'user'), // your collection name
      where('email', '==', email)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null; // no user found
    }

    // Assuming emails are unique, return the first document
    const userDoc = querySnapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    console.error('Error fetching user by email:', error);
    return null;
  }
};

export const getBusinessUserByEmail = async (email) => {
  try {
    const q = query(
      collection(fireStoreDB, 'businesses'), // your collection name
      where('email', '==', email)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null; // no user found
    }

    // Assuming emails are unique, return the first document
    const userDoc = querySnapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    console.error('Error fetching user by email:', error);
    return null;
  }
};

// export const getUserByCompanyName = async (companyName) => {

//   try {
//     const q = query(
//       collection(fireStoreDB, 'user'),
//       where('company', '==', companyName)
//     );

//     const querySnapshot = await getDocs(q);

//     if (querySnapshot.empty) {
//       return []; // return empty array instead of null
//     }

//     // Return all users with matching companyName
//     const users = querySnapshot.docs.map((doc) => ({
//       id: doc.id,
//       ...doc.data()
//     }));

//     return users;

//   } catch (error) {
//     console.error('Error fetching users by company name:', error);
//     return [];
//   }
// };

// ── Business trips (Duty of Care per-trip records) ──────────────────────────────
// Trips are stored in the "trips" collection keyed by lowercased company name,
// matching how employees are keyed by `company`. NOTE: Firestore security rules
// must allow business users to read/write the "trips" collection.

export const addTrip = async (tripData) => {
  try {
    const ref = collection(fireStoreDB, 'trips');
    const docRef = await addDoc(ref, {
      ...tripData,
      company: (tripData.company || '').toLowerCase(),
      createdAt: new Date().toISOString(),
    });
    return { id: docRef.id, ...tripData };
  } catch (error) {
    console.error('Error adding trip:', error);
    return null;
  }
};

export const getTripsByCompany = async (companyName) => {
  try {
    if (!companyName) return [];
    const ref = collection(fireStoreDB, 'trips');
    const q = query(ref, where('company', '==', companyName.toLowerCase()));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching trips by company:', error);
    return [];
  }
};

export const deleteTrip = async (id) => {
  try {
    await deleteDoc(doc(fireStoreDB, 'trips', id));
    return true;
  } catch (error) {
    console.error('Error deleting trip:', error);
    return false;
  }
};
